// The monthly rebill check on the dashboard home: once a prop account has been open a month
// (and every month after), it asks whether the firm charged it again. A yes adds the charge to
// the trader's expenses, tied to the account, so My Trading Journey counts what it cost.
import { useEffect, useState } from 'react';
import { api } from './api';
import { featureOpen } from './AppNav';
import { rebillsDue } from './business';
import { useI18n } from './i18n';
import { currentLocale } from './langState';

const longDay = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric' });

function RebillRow({ item, onDone, onSessionExpired }) {
  const { t } = useI18n();
  const { account, due, firm } = item;
  const [amount, setAmount] = useState(item.amount ? String(item.amount) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function answer(kind) {
    if (kind === 'yes' && !(Number(amount) > 0)) {
      setError(t('Enter what it charged.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await api(`/accounts/${account._id}/rebill`, {
        method: 'POST',
        body: { due, answer: kind, amount: kind === 'yes' ? Number(amount) : undefined, firm, note: t('Monthly rebill') },
      });
      onDone(res.account, kind === 'yes' ? Number(amount) : 0);
    } catch (err) {
      if (err.status === 401 && onSessionExpired) onSessionExpired();
      else setError(err.message || t('Could not save your answer. Please try again.'));
      setBusy(false);
    }
  }

  return (
    <li>
      <span className="home-goals-title">{account.name}</span>
      <p className="home-goals-why">
        {t('Its billing day was {day}. Did {firm} charge you again?', { day: longDay(due), firm: firm || t('the firm') })}
      </p>
      <div className="rebill-actions">
        <label className="rebill-amount">
          <span aria-hidden="true">$</span>
          <input type="number" min="0" step="any" inputMode="decimal" value={amount} placeholder={t('e.g. 49')} aria-label={t('Amount (USD)')}
            onChange={(e) => setAmount(e.target.value)} disabled={busy} />
        </label>
        <button type="button" className="small" onClick={() => answer('yes')} disabled={busy}>{t('Yes, it rebilled')}</button>
        <button type="button" className="secondary small" onClick={() => answer('no')} disabled={busy}>{t('No')}</button>
        <button type="button" className="link-button" onClick={() => answer('never')} disabled={busy}>{t('This account doesn’t rebill')}</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </li>
  );
}

export default function RebillCheck({ user, accounts, onAccountChange, onSessionExpired }) {
  const { t } = useI18n();
  const [expenses, setExpenses] = useState(null);
  const [answered, setAnswered] = useState([]);
  const [added, setAdded] = useState(0);

  useEffect(() => {
    let live = true;
    api('/expenses').then((list) => live && setExpenses(list)).catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  if (!expenses) return null;
  const due = rebillsDue(accounts, expenses).filter((d) => !answered.includes(d.account._id));
  if (!due.length && !added) return null;
  const preview = !featureOpen(user, 'journey');

  function done(account, amount) {
    setAnswered((list) => [...list, account._id]);
    if (amount) {
      setAdded((n) => n + amount);
      window.dispatchEvent(new CustomEvent('mm-expenses-changed'));
    }
    if (onAccountChange) onAccountChange(account);
  }

  return (
    <section className={`home-goals rebill-check${preview ? ' admin-preview-card' : ''}`} aria-labelledby="rebill-title">
      <div className="home-goals-head">
        <div>
          <span className="home-goals-label rebill-label">{t('Monthly rebill')}</span>
          <h2 id="rebill-title">{due.length ? t(due.length === 1 ? 'An account has been open a month' : 'Some accounts have been open a month') : t('Thanks, your spending is up to date')}</h2>
        </div>
        {preview && <span className="admin-preview-badge">Admin only</span>}
      </div>
      {due.length > 0 && <p className="home-goals-why">{t('Prop firms often charge evaluations again every month. Tell us if this one did, so My Trading Journey counts what it really cost.')}</p>}
      {added > 0 && (
        <p className="home-goals-why rebill-saved">{t('Added {amount} to your spending in My Trading Journey.', { amount: `$${added.toLocaleString(currentLocale())}` })}</p>
      )}
      {due.length > 0 && (
        <ul>
          {due.map((item) => <RebillRow key={`${item.account._id}-${item.due}`} item={item} onDone={done} onSessionExpired={onSessionExpired} />)}
        </ul>
      )}
    </section>
  );
}
